import { useState, useEffect } from 'react';
import { Sparkles, AlertTriangle, Plus, Trash2, ChevronDown, ChevronUp, GitCompare } from 'lucide-react';
import Modal from './Modal';
import { apiClient } from '../lib/apiClient';
import { formatCurrency } from '../lib/utils';
import type { Project } from '../types';

interface AiStage {
  title: string;
  description: string;
  plannedStart: string | null;
  plannedEnd: string | null;
}

interface AiInvoice {
  title: string;
  amount: number;
  vatRate: number;
  plannedSendDate: string | null;
  isOptional?: boolean;
  optionalNote?: string | null;
}

interface AiProject {
  name: string | null;
  clientName: string | null;
  description: string | null;
  value: number | null;
  currency: string;
  contractSigningDate: string | null;
  startDate: string | null;
  endDate: string | null;
  estimatedDurationWeeks: number | null;
  scope: string | null;
  salesPerson: string | null;
  paymentTerms: string | null;
  importantNotes: string | null;
}

interface AiResult {
  confidence: 'high' | 'medium' | 'low';
  warnings: string[];
  project: AiProject;
  stages: AiStage[];
  invoices: AiInvoice[];
}

interface ChangedField {
  field: string;
  label: string;
  old: string;
  new: string;
}

interface Props {
  open: boolean;
  documentId: string;
  projectName: string;
  currentProject?: Project;
  onClose: () => void;
  onApplied: () => void;
}

const confidenceColor = { high: 'text-green-600', medium: 'text-amber-600', low: 'text-red-500' };
const confidenceLabel = { high: 'High confidence', medium: 'Medium confidence', low: 'Low confidence — review carefully' };

const inputCls = 'w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500';

function detectChanges(result: AiResult, current: Project): ChangedField[] {
  const changes: ChangedField[] = [];
  const fmt = (v: string | number | null | undefined) => String(v ?? '—');

  if (result.project.name && result.project.name !== current.name) {
    changes.push({ field: 'name', label: 'Project name', old: current.name, new: result.project.name });
  }
  if (result.project.description && result.project.description !== current.description) {
    changes.push({ field: 'description', label: 'Description', old: current.description || '—', new: result.project.description });
  }
  if (result.project.value != null && Math.abs(result.project.value - Number(current.value)) > 0.01) {
    changes.push({ field: 'value', label: 'Total project value', old: fmt(Number(current.value)), new: fmt(result.project.value) });
  }
  if (result.project.startDate && result.project.startDate !== current.startDate) {
    changes.push({ field: 'startDate', label: 'Start date', old: current.startDate, new: result.project.startDate });
  }
  if (result.project.endDate && result.project.endDate !== current.endDate) {
    changes.push({ field: 'endDate', label: 'End date', old: current.endDate, new: result.project.endDate });
  }
  return changes;
}

export default function AiReviewModal({ open, documentId, projectName, currentProject, onClose, onApplied }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorTitle, setErrorTitle] = useState('Extraction failed');
  const [result, setResult] = useState<AiResult | null>(null);
  const [applying, setApplying] = useState(false);
  const [changes, setChanges] = useState<ChangedField[]>([]);

  // Editable state derived from AI result
  const [projectEdits, setProjectEdits] = useState<Partial<AiProject>>({});
  const [stages, setStages] = useState<AiStage[]>([]);
  const [invoices, setInvoices] = useState<AiInvoice[]>([]);
  const [replaceStages, setReplaceStages] = useState(true);
  const [replaceInvoices, setReplaceInvoices] = useState(true);

  const [showScope, setShowScope] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  useEffect(() => {
    if (!open || !documentId) return;
    setLoading(true);
    setError('');
    setErrorTitle('Extraction failed');
    setResult(null);
    setChanges([]);
    apiClient.post<{ ok: boolean; result: AiResult; previousResult: AiResult | null }>(`/documents/${documentId}/extract`, {})
      .then(({ result: r }) => {
        setResult(r);
        setProjectEdits({
          name: r.project.name ?? undefined,
          description: r.project.description ?? undefined,
          value: r.project.value ?? undefined,
          startDate: r.project.startDate ?? undefined,
          endDate: r.project.endDate ?? undefined,
        });
        setStages(r.stages);
        // Only pre-select non-optional invoices by default
        setInvoices(r.invoices.filter((i) => !i.isOptional));
        if (currentProject) {
          setChanges(detectChanges(r, currentProject));
        }
      })
      .catch((e) => setError(e?.message ?? 'AI extraction failed. Please try again.'))
      .finally(() => setLoading(false));
  }, [open, documentId]);

  const handleApply = async () => {
    setApplying(true);
    try {
      await apiClient.post(`/documents/${documentId}/apply`, {
        project: {
          name: projectEdits.name || undefined,
          description: projectEdits.description ?? undefined,
          value: projectEdits.value || undefined,
          startDate: projectEdits.startDate || undefined,
          endDate: projectEdits.endDate || undefined,
        },
        replaceStages,
        stages: stages.map((s) => ({ title: s.title, description: s.description, plannedStart: s.plannedStart, plannedEnd: s.plannedEnd })),
        replaceInvoices,
        invoices: invoices.map((i) => ({ title: i.title, amount: i.amount, vatRate: i.vatRate, plannedSendDate: i.plannedSendDate })),
      });
      onApplied();
      onClose();
    } catch (e: unknown) {
      setErrorTitle('Failed to apply');
      setError((e as Error)?.message ?? 'Failed to apply changes. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const updateStage = (i: number, field: keyof AiStage, val: string) =>
    setStages((prev) => prev.map((s, idx) => idx === i ? { ...s, [field]: val || null } : s));
  const removeStage = (i: number) => setStages((prev) => prev.filter((_, idx) => idx !== i));
  const addStage = () => setStages((prev) => [...prev, { title: 'New Stage', description: '', plannedStart: null, plannedEnd: null }]);

  const updateInvoice = (i: number, field: keyof AiInvoice, val: string | number) =>
    setInvoices((prev) => prev.map((inv, idx) => idx === i ? { ...inv, [field]: val } : inv));
  const removeInvoice = (i: number) => setInvoices((prev) => prev.filter((_, idx) => idx !== i));
  const addInvoice = () => setInvoices((prev) => [...prev, { title: 'Invoice', amount: 0, vatRate: 24, plannedSendDate: null }]);

  const optionalInvoices = result?.invoices.filter((i) => i.isOptional) ?? [];

  return (
    <Modal open={open} onClose={onClose} title="AI Document Extraction" size="xl">
      <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
        {loading && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="w-10 h-10 rounded-full border-4 border-brand-500 border-t-transparent animate-spin" />
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700">Analysing document with Claude…</p>
              <p className="text-xs text-gray-400 mt-1">Extracting project info, stages and payment schedule</p>
            </div>
          </div>
        )}

        {error && !loading && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-xl">
            <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-700 font-medium">{errorTitle}</p>
              <p className="text-xs text-red-500 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {result && !loading && (
          <>
            {/* Confidence + warnings */}
            <div className="flex items-center gap-3 px-3 py-2.5 bg-brand-50 border border-brand-100 rounded-xl">
              <Sparkles size={15} className="text-brand-500 shrink-0" />
              <div className="flex-1">
                <span className={`text-xs font-semibold ${confidenceColor[result.confidence]}`}>
                  {confidenceLabel[result.confidence]}
                </span>
                <span className="text-xs text-gray-400 ml-2">· {projectName}</span>
              </div>
            </div>
            {result.warnings.length > 0 && (
              <div className="px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-xl space-y-1">
                {result.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-700 flex items-start gap-1.5">
                    <AlertTriangle size={11} className="shrink-0 mt-0.5" /> {w}
                  </p>
                ))}
              </div>
            )}

            {/* Change detection — what's different from current project */}
            {changes.length > 0 && (
              <div className="px-3 py-3 bg-blue-50 border border-blue-100 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <GitCompare size={13} className="text-blue-500" />
                  <span className="text-xs font-semibold text-blue-700">{changes.length} change{changes.length !== 1 ? 's' : ''} detected vs current project</span>
                </div>
                <div className="space-y-1">
                  {changes.map((c) => (
                    <div key={c.field} className="text-xs text-blue-600 flex items-baseline gap-2">
                      <span className="font-medium shrink-0">{c.label}:</span>
                      <span className="line-through text-blue-300 truncate max-w-[160px]">{c.old}</span>
                      <span>→</span>
                      <span className="font-medium truncate max-w-[160px]">{c.new}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Project info */}
            <section>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Project Info</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs text-gray-400 block mb-1">Project name</label>
                  <input value={projectEdits.name ?? ''} onChange={(e) => setProjectEdits((p) => ({ ...p, name: e.target.value }))} className={inputCls} placeholder="—" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-400 block mb-1">Description</label>
                  <textarea value={projectEdits.description ?? ''} onChange={(e) => setProjectEdits((p) => ({ ...p, description: e.target.value }))} className={`${inputCls} resize-none`} rows={2} placeholder="—" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Total project value (€)</label>
                  <input type="number" value={projectEdits.value ?? ''} onChange={(e) => setProjectEdits((p) => ({ ...p, value: Number(e.target.value) || undefined }))} className={inputCls} placeholder="—" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Start date</label>
                  <input type="date" value={projectEdits.startDate ?? ''} onChange={(e) => setProjectEdits((p) => ({ ...p, startDate: e.target.value || null }))} className={inputCls} />
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">End date</label>
                  <input type="date" value={projectEdits.endDate ?? ''} onChange={(e) => setProjectEdits((p) => ({ ...p, endDate: e.target.value || null }))} className={inputCls} />
                </div>
                {result.project.estimatedDurationWeeks && (
                  <div className="flex items-center gap-2 col-span-2">
                    <span className="text-xs text-gray-400">Estimated duration:</span>
                    <span className="text-xs font-medium text-gray-700">{result.project.estimatedDurationWeeks} weeks</span>
                  </div>
                )}
              </div>

              {/* Read-only info */}
              <div className="mt-2 space-y-1">
                {result.project.clientName && (
                  <p className="text-xs text-gray-400">Client: <strong className="text-gray-600">{result.project.clientName}</strong></p>
                )}
                {result.project.salesPerson && (
                  <p className="text-xs text-gray-400">Salesperson: <span className="text-gray-600">{result.project.salesPerson}</span></p>
                )}
                {result.project.contractSigningDate && (
                  <p className="text-xs text-gray-400">Contract signed: <span className="text-gray-600">{result.project.contractSigningDate}</span></p>
                )}
                {result.project.paymentTerms && (
                  <p className="text-xs text-gray-400">Payment terms: <span className="text-gray-600">{result.project.paymentTerms}</span></p>
                )}
              </div>

              {result.project.scope && (
                <div className="mt-2">
                  <button onClick={() => setShowScope((v) => !v)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
                    {showScope ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Scope summary
                  </button>
                  {showScope && <p className="text-xs text-gray-500 mt-1 leading-relaxed bg-gray-50 rounded-lg p-3">{result.project.scope}</p>}
                </div>
              )}
              {result.project.importantNotes && (
                <div className="mt-1">
                  <button onClick={() => setShowNotes((v) => !v)} className="flex items-center gap-1 text-xs text-amber-500 hover:text-amber-700">
                    {showNotes ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Important contract notes
                  </button>
                  {showNotes && <p className="text-xs text-amber-700 mt-1 leading-relaxed bg-amber-50 rounded-lg p-3">{result.project.importantNotes}</p>}
                </div>
              )}
            </section>

            {/* Stages */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Stages <span className="text-gray-300 font-normal ml-1">({stages.length})</span>
                </h3>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                    <input type="checkbox" checked={replaceStages} onChange={(e) => setReplaceStages(e.target.checked)} className="rounded" />
                    Replace existing
                  </label>
                  <button onClick={addStage} className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-700">
                    <Plus size={12} /> Add
                  </button>
                </div>
              </div>
              {stages.length === 0 && <p className="text-xs text-gray-400 py-2">No stages extracted. Add manually or the AI could not detect them.</p>}
              <div className="space-y-2">
                {stages.map((s, i) => (
                  <div key={i} className="border border-gray-100 rounded-xl p-3 bg-gray-50/50">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <input value={s.title} onChange={(e) => updateStage(i, 'title', e.target.value)} className={inputCls} placeholder="Stage title" />
                        <input value={s.description} onChange={(e) => updateStage(i, 'description', e.target.value)} className={inputCls} placeholder="Description" />
                        <input type="date" value={s.plannedStart ?? ''} onChange={(e) => updateStage(i, 'plannedStart', e.target.value)} className={inputCls} />
                        <input type="date" value={s.plannedEnd ?? ''} onChange={(e) => updateStage(i, 'plannedEnd', e.target.value)} className={inputCls} />
                      </div>
                      <button onClick={() => removeStage(i)} className="p-1.5 text-gray-300 hover:text-red-500 rounded-lg shrink-0">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Invoices */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Planned Invoices <span className="text-gray-300 font-normal ml-1">({invoices.length})</span>
                </h3>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                    <input type="checkbox" checked={replaceInvoices} onChange={(e) => setReplaceInvoices(e.target.checked)} className="rounded" />
                    Replace existing
                  </label>
                  <button onClick={addInvoice} className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-700">
                    <Plus size={12} /> Add
                  </button>
                </div>
              </div>
              {invoices.length === 0 && <p className="text-xs text-gray-400 py-2">No invoices extracted.</p>}
              <div className="space-y-2">
                {invoices.map((inv, i) => (
                  <div key={i} className="border border-gray-100 rounded-xl p-3 bg-gray-50/50">
                    <div className="flex items-center gap-2">
                      <input value={inv.title} onChange={(e) => updateInvoice(i, 'title', e.target.value)} className={`${inputCls} flex-1`} placeholder="Invoice title" />
                      <input type="number" value={inv.amount || ''} onChange={(e) => updateInvoice(i, 'amount', Number(e.target.value))} className={`${inputCls} w-28`} placeholder="Amount" />
                      <input type="number" value={inv.vatRate} onChange={(e) => updateInvoice(i, 'vatRate', Number(e.target.value))} className={`${inputCls} w-20`} placeholder="VAT%" />
                      <input type="date" value={inv.plannedSendDate ?? ''} onChange={(e) => updateInvoice(i, 'plannedSendDate', e.target.value)} className={`${inputCls} w-36`} />
                      <button onClick={() => removeInvoice(i)} className="p-1.5 text-gray-300 hover:text-red-500 rounded-lg shrink-0">
                        <Trash2 size={13} />
                      </button>
                    </div>
                    {inv.amount > 0 && (
                      <p className="text-xs text-gray-400 mt-1 ml-0.5">
                        {formatCurrency(inv.amount)} + {inv.vatRate}% VAT = {formatCurrency(inv.amount * (1 + inv.vatRate / 100))}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              {invoices.length > 0 && (
                <p className="text-xs text-gray-400 mt-2 text-right">
                  Total: {formatCurrency(invoices.reduce((s, i) => s + i.amount, 0))} + VAT
                </p>
              )}

              {/* Optional invoices (not added by default) */}
              {optionalInvoices.length > 0 && (
                <div className="mt-3 px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-xl">
                  <p className="text-xs font-semibold text-gray-500 mb-1.5">Optional invoices (not added — requires confirmation)</p>
                  {optionalInvoices.map((inv, i) => (
                    <div key={i} className="flex items-center justify-between py-1">
                      <div>
                        <span className="text-xs text-gray-600">{inv.title}</span>
                        {inv.optionalNote && <span className="text-xs text-gray-400 ml-2">— {inv.optionalNote}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-700">{formatCurrency(inv.amount)}</span>
                        <button
                          onClick={() => setInvoices((prev) => [...prev, { ...inv, isOptional: false }])}
                          className="text-xs text-brand-500 hover:text-brand-700 font-medium"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {result && !loading && (
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-100 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={applying}
            className="flex items-center gap-2 px-5 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium disabled:opacity-60"
          >
            <Sparkles size={13} />
            {applying ? 'Applying…' : 'Apply to project'}
          </button>
        </div>
      )}

      {!result && !loading && error && (
        <div className="flex justify-end pt-4 border-t border-gray-100 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
            Close
          </button>
        </div>
      )}
    </Modal>
  );
}
